import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaSearch, FaFilter, FaTrophy, FaCalendarAlt, FaUsers, FaGamepad, FaClock, FaDollarSign, FaPlus } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext.js';

const Tournaments = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { currentUser } = useAuth();
  
  // Form state for tournament creation
  const [tournamentForm, setTournamentForm] = useState({
    title: '',
    game: '',
    startDate: '',
    endDate: '',
    description: '',
    prize: '',
    maxParticipants: 8,
    participantType: 'individual', // 'individual', 'team', or 'both'
    minTeamSize: 1,
    maxTeamSize: 5,
    registrationDeadline: '',
    rules: '',
    streamRequired: false,
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTournamentForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error for this field when user edits it
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  
  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (!tournamentForm.title.trim()) errors.title = 'Tournament title is required';
    if (!tournamentForm.game.trim()) errors.game = 'Game is required';
    if (!tournamentForm.startDate) errors.startDate = 'Start date is required';
    if (!tournamentForm.registrationDeadline) errors.registrationDeadline = 'Registration deadline is required';
    
    // Validate dates
    const now = new Date();
    const startDate = new Date(tournamentForm.startDate);
    const regDeadline = new Date(tournamentForm.registrationDeadline);
    
    if (regDeadline < now) {
      errors.registrationDeadline = 'Registration deadline cannot be in the past';
    }
    
    if (startDate < now) {
      errors.startDate = 'Start date cannot be in the past';
    }
    
    if (regDeadline > startDate) {
      errors.registrationDeadline = 'Registration deadline must be before the start date';
    }
    
    // Team size validation for team tournaments
    if (tournamentForm.participantType !== 'individual') {
      if (parseInt(tournamentForm.minTeamSize) < 1) {
        errors.minTeamSize = 'Minimum team size must be at least 1';
      }
      
      if (parseInt(tournamentForm.maxTeamSize) < parseInt(tournamentForm.minTeamSize)) {
        errors.maxTeamSize = 'Maximum team size cannot be less than minimum team size';
      }
    }
    
    return errors;
  };
  
  // Submit the form
  const handleCreateTournament = async (e) => {
    e.preventDefault();
    
    // Validate the form
    const errors = validateForm();
    setFormErrors(errors);
    
    // If there are errors, don't submit
    if (Object.keys(errors).length > 0) return;
    
    setIsSubmitting(true);
    
    try {
      // Get Firestore reference
      const { db } = await import('../firebase.js').then(module => module.getServices());
      const firebase = await import('firebase/compat/app');
      
      // Create a new tournament document in Firestore
      const tournamentData = {
        title: tournamentForm.title,
        game: tournamentForm.game,
        startDate: firebase.default.firestore.Timestamp.fromDate(new Date(tournamentForm.startDate)),
        endDate: tournamentForm.endDate ? firebase.default.firestore.Timestamp.fromDate(new Date(tournamentForm.endDate)) : null,
        description: tournamentForm.description,
        prizePool: parseFloat(tournamentForm.prize) || 0,
        maxParticipants: parseInt(tournamentForm.maxParticipants),
        participantType: tournamentForm.participantType,
        minTeamSize: parseInt(tournamentForm.minTeamSize),
        maxTeamSize: parseInt(tournamentForm.maxTeamSize),
        registrationDeadline: tournamentForm.registrationDeadline ? 
          firebase.default.firestore.Timestamp.fromDate(new Date(tournamentForm.registrationDeadline)) : null,
        rules: tournamentForm.rules,
        streamRequired: tournamentForm.streamRequired,
        createdBy: currentUser.uid,
        createdAt: firebase.default.firestore.FieldValue.serverTimestamp(),
        participants: [],
        teams: [],
        status: 'upcoming'
      };
      
      // Generate bracket structure if it's a single or double elimination tournament
      if (['single-elimination', 'double-elimination'].includes(tournamentForm.format)) {
        // Import the generateBrackets function
        const { generateBrackets } = await import('../utils/tournamentUtils.js');
        const bracketSize = Math.pow(2, Math.ceil(Math.log2(parseInt(tournamentForm.maxParticipants))));
        tournamentData.brackets = generateBrackets(tournamentForm.format, bracketSize);
      }
      
      // Add to Firestore
      const docRef = await db.collection('tournaments').add(tournamentData);
      
      // Add creator as a participant
      await db.collection('tournaments').doc(docRef.id).collection('participants').doc(currentUser.uid).set({
        userId: currentUser.uid,
        joinedAt: firebase.default.firestore.FieldValue.serverTimestamp(),
        role: 'creator'
      });
      
      // Show success message
      setSubmitSuccess(true);
      
      // Reset form after 2 seconds and close modal
      setTimeout(() => {
        setTournamentForm({
          title: '',
          game: '',
          startDate: '',
          endDate: '',
          description: '',
          prize: '',
          maxParticipants: 8,
          participantType: 'individual',
          minTeamSize: 1,
          maxTeamSize: 5,
          registrationDeadline: '',
          rules: '',
          streamRequired: false,
        });
        setSubmitSuccess(false);
        setShowCreateModal(false);
      }, 2000);
      
    } catch (error) {
      console.error("Error creating tournament:", error);
      setFormErrors(prev => ({ ...prev, submit: `Failed to create tournament: ${error.message}` }));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Mock tournament data
  const tournaments = [
    { 
      id: 1, 
      title: 'Apex Legends Championship', 
      game: 'Apex Legends', 
      startDate: '2025-05-15', 
      status: 'upcoming', 
      participants: 128, 
      prize: '$50,000', 
      registrationOpen: true 
    },
    { 
      id: 2, 
      title: 'Valorant Invitational', 
      game: 'Valorant', 
      startDate: '2025-04-30', 
      status: 'upcoming', 
      participants: 64, 
      prize: '$25,000', 
      registrationOpen: true 
    },
    { 
      id: 3, 
      title: 'Fortnite Solo Cup', 
      game: 'Fortnite', 
      startDate: '2025-05-10', 
      status: 'upcoming', 
      participants: 256, 
      prize: '$100,000', 
      registrationOpen: true 
    },
    { 
      id: 4, 
      title: 'CS2 Pro League', 
      game: 'Counter-Strike 2', 
      startDate: '2025-06-01', 
      status: 'upcoming', 
      participants: 32, 
      prize: '$75,000', 
      registrationOpen: false 
    },
    { 
      id: 5, 
      title: 'Minecraft Building Competition', 
      game: 'Minecraft', 
      startDate: '2025-05-20', 
      status: 'upcoming', 
      participants: 50, 
      prize: '$5,000', 
      registrationOpen: true 
    },
    { 
      id: 6, 
      title: 'League of Legends Spring Championship', 
      game: 'League of Legends', 
      startDate: '2025-04-20', 
      status: 'live', 
      participants: 16, 
      prize: '$200,000', 
      registrationOpen: false 
    },
    { 
      id: 7, 
      title: 'Rocket League Tournament', 
      game: 'Rocket League', 
      startDate: '2025-05-05', 
      status: 'upcoming', 
      participants: 24, 
      prize: '$15,000', 
      registrationOpen: true 
    },
    { 
      id: 8, 
      title: 'Call of Duty Warzone Showdown', 
      game: 'Call of Duty: Warzone', 
      startDate: '2025-03-25', 
      status: 'completed', 
      participants: 150, 
      prize: '$30,000', 
      registrationOpen: false 
    }
  ];

  // Filter tournaments based on search query and filter selection
  const filteredTournaments = tournaments.filter(tournament => {
    // Filter by search query
    const matchesSearch = 
      tournament.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tournament.game.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Filter by status
    const matchesFilter = 
      filter === 'all' || 
      tournament.status === filter || 
      (filter === 'open' && tournament.registrationOpen);
    
    return matchesSearch && matchesFilter;
  });
  
  // Sort tournaments by date (upcoming first, then live, then completed)
  const sortedTournaments = [...filteredTournaments].sort((a, b) => {
    // Priority order: live > upcoming > completed
    const statusOrder = { live: 0, upcoming: 1, completed: 2 };
    const statusDiff = statusOrder[a.status] - statusOrder[b.status];
    
    if (statusDiff !== 0) return statusDiff;

    // If same status, sort by date
    return new Date(a.startDate) - new Date(b.startDate);
  });

  // Format date for display
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  return (
    <div className="min-h-screen bg-harmony-blue">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <motion.h1 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-3xl font-bold text-harmony-white"
          >
            Tournaments
          </motion.h1>

          {currentUser && (
            <motion.button
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 bg-harmony-orange hover:bg-harmony-orange/90 text-white py-2 px-4 rounded-lg transition-colors"
            >
              <FaPlus size={14} />
              <span>Create Tournament</span>
            </motion.button>
          )}
        </div>
        
        {/* Search and filter section */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-grow">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="text-harmony-white/50" />
            </div>
            <input
              type="text"
              placeholder="Search tournaments or games..."
              className="w-full bg-harmony-blue-dark border border-harmony-white/10 text-harmony-white pl-10 pr-4 py-3 rounded-lg focus:outline-none focus:border-harmony-orange"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex gap-2 overflow-x-auto">
            <button 
              onClick={() => setFilter('all')} 
              className={`px-4 py-3 rounded-lg flex items-center whitespace-nowrap ${
                filter === 'all' 
                  ? 'bg-harmony-orange text-harmony-white' 
                  : 'bg-harmony-blue-dark text-harmony-white/70 hover:bg-harmony-blue-dark/80'
              }`}
            >
              <FaFilter className="mr-2" /> All Tournaments
            </button>
            <button 
              onClick={() => setFilter('live')} 
              className={`px-4 py-3 rounded-lg flex items-center whitespace-nowrap ${
                filter === 'live' 
                  ? 'bg-harmony-orange text-harmony-white' 
                  : 'bg-harmony-blue-dark text-harmony-white/70 hover:bg-harmony-blue-dark/80'
              }`}
            >
              Live Now
            </button>
            <button 
              onClick={() => setFilter('upcoming')} 
              className={`px-4 py-3 rounded-lg flex items-center whitespace-nowrap ${
                filter === 'upcoming' 
                  ? 'bg-harmony-orange text-harmony-white' 
                  : 'bg-harmony-blue-dark text-harmony-white/70 hover:bg-harmony-blue-dark/80'
              }`}
            >
              Upcoming
            </button>
            <button 
              onClick={() => setFilter('open')} 
              className={`px-4 py-3 rounded-lg flex items-center whitespace-nowrap ${
                filter === 'open' 
                  ? 'bg-harmony-orange text-harmony-white' 
                  : 'bg-harmony-blue-dark text-harmony-white/70 hover:bg-harmony-blue-dark/80'
              }`}
            >
              Registration Open
            </button>
            <button 
              onClick={() => setFilter('completed')} 
              className={`px-4 py-3 rounded-lg flex items-center whitespace-nowrap ${
                filter === 'completed' 
                  ? 'bg-harmony-orange text-harmony-white' 
                  : 'bg-harmony-blue-dark text-harmony-white/70 hover:bg-harmony-blue-dark/80'
              }`}
            >
              Completed
            </button>
          </div>
        </div>
        
        {/* Tournaments list */}
        {sortedTournaments.length > 0 ? (
          <div className="space-y-4">
            {sortedTournaments.map((tournament, index) => (
              <motion.div
                key={tournament.id}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.05 * (index % 8) }}
                className="bg-harmony-blue-dark rounded-lg overflow-hidden border border-harmony-white/10 hover:border-harmony-orange/30 transition-all"
              >
                <div className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex items-start">
                      <div className="bg-harmony-blue p-3 rounded-lg mr-4 text-harmony-orange">
                        <FaTrophy size={24} />
                      </div>
                      <div>
                        <div className="flex items-center">
                          <h3 className="text-xl font-semibold text-harmony-white mr-3">{tournament.title}</h3>
                          {tournament.status === 'live' && (
                            <span className="bg-red-600 text-white text-xs px-2 py-1 rounded uppercase tracking-wider">
                              Live
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-2">
                          <div className="flex items-center text-harmony-white/70">
                            <FaGamepad className="mr-1" /> {tournament.game}
                          </div>
                          <div className="flex items-center text-harmony-white/70">
                            <FaCalendarAlt className="mr-1" /> {formatDate(tournament.startDate)}
                          </div>
                          <div className="flex items-center text-harmony-white/70">
                            <FaUsers className="mr-1" /> {tournament.participants} Participants
                          </div>
                          <div className="flex items-center text-harmony-white/70">
                            <FaDollarSign className="mr-1" /> {tournament.prize} Prize Pool
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4 lg:mt-0">
                      {tournament.status === 'upcoming' && tournament.registrationOpen && (
                        <button className="px-6 py-2 bg-harmony-orange text-harmony-white rounded-lg hover:bg-harmony-orange/90 transition-colors">
                          Join Tournament
                        </button>
                      )}
                      {tournament.status === 'live' && (
                        <button className="px-6 py-2 bg-red-600 text-harmony-white rounded-lg hover:bg-red-700 transition-colors">
                          Watch Live
                        </button>
                      )}
                      <Link 
                        to={`/tournament/${tournament.id}`}
                        className="px-6 py-2 bg-harmony-blue/30 text-harmony-white rounded-lg hover:bg-harmony-blue/40 transition-colors"
                      >
                        View Details
                      </Link>
                    </div>
                  </div>
                  
                  {/* Registration status */}
                  {tournament.status === 'upcoming' && (
                    <div className="mt-4 pt-4 border-t border-harmony-white/10">
                      <div className="flex items-center text-sm">
                        <FaClock className="mr-2 text-harmony-white/50" />
                        {tournament.registrationOpen ? (
                          <span className="text-green-400">Registration is open</span>
                        ) : (
                          <span className="text-yellow-400">Registration closed</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="text-harmony-white/50 text-5xl mb-4">
              <FaTrophy />
            </div>
            <h3 className="text-harmony-white text-xl font-medium mb-2">No tournaments found</h3>
            <p className="text-harmony-white/70">Try adjusting your search or filter settings.</p>
          </div>
        )}
      </div>

      {/* Create Tournament Modal - Show this when showCreateModal is true */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-harmony-blue-dark w-full max-w-4xl rounded-xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-harmony-white">Create Tournament</h2>
              <button 
                onClick={() => setShowCreateModal(false)}
                className="text-harmony-white/70 hover:text-harmony-white"
              >
                ✕
              </button>
            </div>
            
            {submitSuccess ? (
              <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-4 text-center">
                <FaTrophy className="text-green-400 text-3xl mx-auto mb-2" />
                <p className="text-green-100 text-lg font-medium">Tournament created successfully!</p>
              </div>
            ) : (
              <form onSubmit={handleCreateTournament}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {/* Basic Tournament Info */}
                  <div className="space-y-4 md:col-span-2">
                    <h3 className="text-harmony-white text-lg font-medium">Tournament Details</h3>
                    
                    <div>
                      <label className="block text-harmony-white/80 mb-1 text-sm">Tournament Title*</label>
                      <input
                        type="text"
                        name="title"
                        value={tournamentForm.title}
                        onChange={handleInputChange}
                        className={`w-full bg-harmony-blue-light border ${formErrors.title ? 'border-red-500' : 'border-harmony-white/20'} text-harmony-white px-4 py-2 rounded-lg focus:outline-none focus:border-harmony-orange`}
                        placeholder="e.g. Summer Gaming Championship"
                      />
                      {formErrors.title && <p className="text-red-400 text-xs mt-1">{formErrors.title}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-harmony-white/80 mb-1 text-sm">Game*</label>
                      <input
                        type="text"
                        name="game"
                        value={tournamentForm.game}
                        onChange={handleInputChange}
                        className={`w-full bg-harmony-blue-light border ${formErrors.game ? 'border-red-500' : 'border-harmony-white/20'} text-harmony-white px-4 py-2 rounded-lg focus:outline-none focus:border-harmony-orange`}
                        placeholder="e.g. Valorant, League of Legends"
                      />
                      {formErrors.game && <p className="text-red-400 text-xs mt-1">{formErrors.game}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-harmony-white/80 mb-1 text-sm">Description</label>
                      <textarea
                        name="description"
                        value={tournamentForm.description}
                        onChange={handleInputChange}
                        className="w-full bg-harmony-blue-light border border-harmony-white/20 text-harmony-white px-4 py-2 rounded-lg focus:outline-none focus:border-harmony-orange h-20"
                        placeholder="Describe your tournament..."
                      />
                    </div>
                  </div>
                  
                  {/* Dates and Times */}
                  <div className="space-y-4">
                    <h3 className="text-harmony-white text-lg font-medium">Schedule</h3>
                    
                    <div>
                      <label className="block text-harmony-white/80 mb-1 text-sm">Registration Deadline*</label>
                      <input
                        type="datetime-local"
                        name="registrationDeadline"
                        value={tournamentForm.registrationDeadline}
                        onChange={handleInputChange}
                        className={`w-full bg-harmony-blue-light border ${formErrors.registrationDeadline ? 'border-red-500' : 'border-harmony-white/20'} text-harmony-white px-4 py-2 rounded-lg focus:outline-none focus:border-harmony-orange`}
                      />
                      {formErrors.registrationDeadline && <p className="text-red-400 text-xs mt-1">{formErrors.registrationDeadline}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-harmony-white/80 mb-1 text-sm">Start Date*</label>
                      <input
                        type="datetime-local"
                        name="startDate"
                        value={tournamentForm.startDate}
                        onChange={handleInputChange}
                        className={`w-full bg-harmony-blue-light border ${formErrors.startDate ? 'border-red-500' : 'border-harmony-white/20'} text-harmony-white px-4 py-2 rounded-lg focus:outline-none focus:border-harmony-orange`}
                      />
                      {formErrors.startDate && <p className="text-red-400 text-xs mt-1">{formErrors.startDate}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-harmony-white/80 mb-1 text-sm">End Date (Optional)</label>
                      <input
                        type="datetime-local"
                        name="endDate"
                        value={tournamentForm.endDate}
                        onChange={handleInputChange}
                        className="w-full bg-harmony-blue-light border border-harmony-white/20 text-harmony-white px-4 py-2 rounded-lg focus:outline-none focus:border-harmony-orange"
                      />
                    </div>
                  </div>
                  
                  {/* Participants Configuration */}
                  <div className="space-y-4">
                    <h3 className="text-harmony-white text-lg font-medium">Participants</h3>
                    
                    <div>
                      <label className="block text-harmony-white/80 mb-1 text-sm">Maximum Participants</label>
                      <select
                        name="maxParticipants"
                        value={tournamentForm.maxParticipants}
                        onChange={handleInputChange}
                        className="w-full bg-harmony-blue-light border border-harmony-white/20 text-harmony-white px-4 py-2 rounded-lg focus:outline-none focus:border-harmony-orange"
                      >
                        <option value="4">4</option>
                        <option value="8">8</option>
                        <option value="16">16</option>
                        <option value="32">32</option>
                        <option value="64">64</option>
                        <option value="128">128</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-harmony-white/80 mb-1 text-sm">Participant Type</label>
                      <div className="space-y-2">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="participantType"
                            value="individual"
                            checked={tournamentForm.participantType === 'individual'}
                            onChange={handleInputChange}
                            className="mr-2"
                          />
                          <span className="text-harmony-white/90">Individuals Only</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="participantType"
                            value="team"
                            checked={tournamentForm.participantType === 'team'}
                            onChange={handleInputChange}
                            className="mr-2"
                          />
                          <span className="text-harmony-white/90">Teams Only</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="participantType"
                            value="both"
                            checked={tournamentForm.participantType === 'both'}
                            onChange={handleInputChange}
                            className="mr-2"
                          />
                          <span className="text-harmony-white/90">Both Teams and Individuals</span>
                        </label>
                      </div>
                    </div>
                    
                    {(tournamentForm.participantType === 'team' || tournamentForm.participantType === 'both') && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-harmony-white/80 mb-1 text-sm">Min Team Size</label>
                          <input
                            type="number"
                            name="minTeamSize"
                            value={tournamentForm.minTeamSize}
                            onChange={handleInputChange}
                            min="1"
                            className={`w-full bg-harmony-blue-light border ${formErrors.minTeamSize ? 'border-red-500' : 'border-harmony-white/20'} text-harmony-white px-4 py-2 rounded-lg focus:outline-none focus:border-harmony-orange`}
                          />
                          {formErrors.minTeamSize && <p className="text-red-400 text-xs mt-1">{formErrors.minTeamSize}</p>}
                        </div>
                        <div>
                          <label className="block text-harmony-white/80 mb-1 text-sm">Max Team Size</label>
                          <input
                            type="number"
                            name="maxTeamSize"
                            value={tournamentForm.maxTeamSize}
                            onChange={handleInputChange}
                            min={tournamentForm.minTeamSize}
                            className={`w-full bg-harmony-blue-light border ${formErrors.maxTeamSize ? 'border-red-500' : 'border-harmony-white/20'} text-harmony-white px-4 py-2 rounded-lg focus:outline-none focus:border-harmony-orange`}
                          />
                          {formErrors.maxTeamSize && <p className="text-red-400 text-xs mt-1">{formErrors.maxTeamSize}</p>}
                        </div>
                      </div>
                    )}
                    
                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="streamRequired"
                          checked={tournamentForm.streamRequired}
                          onChange={handleInputChange}
                          className="mr-2"
                        />
                        <span className="text-harmony-white/90">Participants must stream their matches</span>
                      </label>
                    </div>
                  </div>
                  
                  {/* Prizes and Rules */}
                  <div className="space-y-4 md:col-span-2">
                    <h3 className="text-harmony-white text-lg font-medium">Prize and Rules</h3>
                    
                    <div>
                      <label className="block text-harmony-white/80 mb-1 text-sm">Prize Pool</label>
                      <input
                        type="text"
                        name="prize"
                        value={tournamentForm.prize}
                        onChange={handleInputChange}
                        className="w-full bg-harmony-blue-light border border-harmony-white/20 text-harmony-white px-4 py-2 rounded-lg focus:outline-none focus:border-harmony-orange"
                        placeholder="e.g. $5,000"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-harmony-white/80 mb-1 text-sm">Tournament Rules</label>
                      <textarea
                        name="rules"
                        value={tournamentForm.rules}
                        onChange={handleInputChange}
                        className="w-full bg-harmony-blue-light border border-harmony-white/20 text-harmony-white px-4 py-2 rounded-lg focus:outline-none focus:border-harmony-orange h-24"
                        placeholder="Outline the rules for your tournament..."
                      />
                    </div>
                  </div>
                </div>
                
                {formErrors.submit && (
                  <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3 mt-5">
                    <p className="text-red-400 text-sm">{formErrors.submit}</p>
                  </div>
                )}
                
                <div className="flex justify-end gap-3 mt-8">
                  <button 
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="bg-harmony-blue-light hover:bg-harmony-blue text-harmony-white py-2 px-6 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-harmony-orange hover:bg-harmony-orange/90 text-white py-2 px-6 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Creating...
                      </>
                    ) : (
                      <>
                        <FaTrophy size={14} />
                        Create Tournament
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Tournaments; 